import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { printBill } from '../../utils/billPrinter';

const OrdersManagement = () => {
    const MySwal = withReactContent(Swal);
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('active'); // 'active' or 'history'

    // Payment Terminal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentOrder, setPaymentOrder] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [amountGiven, setAmountGiven] = useState(0);
    const [discountValue, setDiscountValue] = useState(0);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('Cash');

    useEffect(() => {
        fetchOrders(false); // Initial fetch shows loading spinner
        const interval = setInterval(() => {
            fetchOrders(true); // Background sync doesn't show loading spinner
        }, 2000);
        return () => clearInterval(interval);
    }, [selectedTab]);

    const fetchOrders = async (isBackgroundSync = false) => {
        if (!isBackgroundSync) {
            setLoading(true);
        }
        try {
            let url = selectedTab === 'active' 
                ? "https://avastar-pos-backend.onrender.com/api/orders/active" 
                : "https://avastar-pos-backend.onrender.com/api/orders";
            const res = await axios.get(url);
            setOrders(res.data);
        } catch (err) {
            console.error("Error fetching orders:", err);
        } finally {
            if (!isBackgroundSync) {
                setLoading(false);
            }
        }
    };

    const settlePayment = async (orderId) => {
        const disc = Math.max(0, Number(discountValue) || 0);
        const maxDisc = paymentOrder.grandTotal;
        if (disc > maxDisc) {
            MySwal.fire("Invalid discount", "Discount cannot exceed the order total.", "error");
            return;
        }
        const finalTotal = Math.max(0, paymentOrder.grandTotal - disc).toFixed(2);
        try {
            await axios.patch(`https://avastar-pos-backend.onrender.com/api/orders/${orderId}/status`, {
                status: 'Completed',
                discount: (paymentOrder.discount || 0) + disc,
                grandTotal: Number(finalTotal),
                paymentMethod: paymentMethod,
                couponCode: disc > 0 ? couponCode : null
            });
            
            setShowPaymentModal(false);
            MySwal.fire('Success', 'Payment completed. Order settled!', 'success');
            fetchOrders();
        } catch (err) {
            MySwal.fire('Error', 'Payment failed', 'error');
        }
    };

    const handleCancelOrder = async (orderId) => {
        const confirm = await MySwal.fire({
            title: 'Cancel Order?',
            text: 'Are you sure you want to cancel this order?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Yes, cancel it!'
        });
        
        if (confirm.isConfirmed) {
            try {
                await axios.patch(`https://avastar-pos-backend.onrender.com/api/orders/${orderId}/cancel`);
                MySwal.fire('Cancelled!', 'The order has been cancelled.', 'success');
                fetchOrders();
            } catch (err) {
                MySwal.fire('Error', 'Failed to cancel the order', 'error');
            }
        }
    };

    const openPaymentTerminal = (order) => {
        setPaymentOrder(order);
        setCouponCode('');
        setAmountGiven(0);
        setDiscountValue(0);
        setPaymentMethod('Cash');
        setShowPaymentModal(true);
    };

    const applyCoupon = async () => {
        try {
            const res = await axios.get(`https://avastar-pos-backend.onrender.com/api/coupons/code/${couponCode}`);
            const coupon = res.data;
            
            let disc = 0;
            if (coupon.type === 'Percentage') {
                const pct = Math.min(100, Math.max(0, Number(coupon.discount) || 0));
                disc = paymentOrder.grandTotal * (pct / 100);
            } else {
                disc = Math.max(0, Number(coupon.discount) || 0);
            }
            disc = Math.min(disc, paymentOrder.grandTotal);
            setDiscountValue(disc);
            MySwal.fire('Coupon Applied', `${coupon.name} added!`, 'success');
        } catch (error) {
            MySwal.fire('Invalid Coupon', error.response?.data?.message || 'Check the code and try again', 'error');
        }
    };

    const calculateChange = () => {
        if (!paymentOrder) return "0.00";
        const disc = Math.max(0, Math.min(Number(discountValue) || 0, paymentOrder.grandTotal));
        const total = Math.max(0, paymentOrder.grandTotal - disc);
        const change = Math.max(0, Number(amountGiven) || 0) - total;
        return change.toFixed(2);
    };

    const effectiveDiscount =
        paymentOrder != null
            ? Math.max(0, Math.min(Number(discountValue) || 0, paymentOrder.grandTotal))
            : 0;
    const totalToPay =
        paymentOrder != null ? Math.max(0, paymentOrder.grandTotal - effectiveDiscount) : 0;



    return (
        <div className="page-wrapper orders-page-wrapper">
            <style>{`
                .orders-page-wrapper .text-dark { color: var(--orders-text, #212529) !important; }
                .orders-page-wrapper .bg-white { background-color: var(--orders-card, #ffffff) !important; }
                .orders-page-wrapper .bg-light { background-color: var(--orders-item-bg, #f8f9fa) !important; }
                .orders-page-wrapper .border-bottom, .orders-page-wrapper .border-top, .orders-page-wrapper .border-light { border-color: var(--orders-border, #e9ecef) !important; }
                .orders-page-wrapper .text-muted { color: var(--orders-text-muted, #6c757d) !important; }
                .payment-terminal-modal { background-color: var(--orders-card, #ffffff) !important; }
                .payment-terminal-side { background-color: var(--orders-item-bg, #f8fafc) !important; }
                .summary-list-item { border-color: var(--orders-border, #f1f5f9) !important; }
                .amount-input { background-color: var(--orders-item-bg, #f8fafc) !important; border-color: var(--orders-border, #e2e8f0) !important; color: var(--orders-text, #212529) !important; }
                
                [data-layout-mode="dark_mode"] .orders-page-wrapper {
                    --orders-card: #1e293b;
                    --orders-item-bg: #0f172a;
                    --orders-text: #ffffff;
                    --orders-text-muted: #cbd5e1;
                    --orders-border: #334155;
                }
            `}</style>
            <div className="content">
                <div className="page-header d-flex justify-content-between align-items-center mb-4">
                    <div className="page-title">
                        <h4 className="fw-bold text-dark">Dine-In & Orders Management</h4>
                        <p className="text-muted mb-0">Track active tables and manage bill settlement</p>
                    </div>
                </div>

                <div className="card border-0 shadow-sm overflow-hidden">
                    <div className="card-header bg-white p-0 border-bottom">
                        <ul className="nav nav-pills p-3 bg-light m-3 rounded-pill" style={{maxWidth: 'fit-content'}}>
                            <li className="nav-item">
                                <button 
                                    className={`nav-link px-4 rounded-pill fw-bold ${selectedTab === 'active' ? 'active shadow-sm' : 'text-secondary'}`} 
                                    onClick={() => setSelectedTab('active')}
                                >
                                    Active Orders <span className="ms-1 px-2 bg-white text-primary rounded-pill small">{selectedTab === 'active' ? orders.length : ''}</span>
                                </button>
                            </li>
                            <li className="nav-item ms-2">
                                <button 
                                    className={`nav-link px-4 rounded-pill fw-bold ${selectedTab === 'history' ? 'active shadow-sm' : 'text-secondary'}`} 
                                    onClick={() => setSelectedTab('history')}
                                >
                                    Order History
                                </button>
                            </li>
                        </ul>
                    </div>
                    
                    <div className="card-body p-4 bg-light bg-opacity-10">
                        {loading ? (
                            <div className="text-center p-5"><span className="spinner-border text-primary"></span></div>
                        ) : orders.length === 0 ? (
                            <div className="text-center p-5 text-muted">No orders found.</div>
                        ) : selectedTab === 'active' ? (
                            <div className="row g-4">
                                {orders.map(order => (
                                    <div className="col-12 col-md-6 col-lg-4" key={order._id}>
                                        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative" style={{borderTop: '5px solid #ff9f43'}}>
                                            <div className="p-3 d-flex justify-content-between align-items-center bg-white border-bottom">
                                                <div>
                                                    <h5 className="mb-0 fw-bold text-primary">{order.kotNumber}</h5>
                                                    <p className="small text-muted mb-0">{order.orderId}</p>
                                                </div>
                                                <div className="text-end">
                                                    <span className={`badge ${order.orderType === 'Dine-in' ? 'bg-primary' : 'bg-info'} bg-opacity-10 ${order.orderType === 'Dine-in' ? 'text-primary' : 'text-info'} px-3 py-1 rounded-pill`}>
                                                        {order.orderType}
                                                    </span>
                                                    {order.tableNumber && (
                                                        <div className="mt-1 fw-bold text-dark">Table: {order.tableNumber}</div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="card-body p-3">
                                                <div className="mb-3">
                                                    {order.items.map((item, idx) => (
                                                        <div className="d-flex justify-content-between mb-2 pb-1 border-bottom border-light align-items-center" key={idx}>
                                                            <div className="d-flex align-items-center">
                                                                <span className="badge bg-light text-dark me-2">{item.quantity}</span>
                                                                <span className="fw-medium">{item.productName}</span>
                                                            </div>
                                                            <span className="text-muted small">LKR {item.total.toFixed(2)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="d-flex justify-content-between mt-auto">
                                                    <span className="text-dark fw-bold h5 mb-0">LKR {order.grandTotal.toFixed(2)}</span>
                                                    <span className="small text-muted">{new Date(order.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-white d-flex flex-column gap-2 border-top">
                                                <button
                                                    className="btn btn-primary d-flex align-items-center justify-content-center w-100 py-2 fw-bold"
                                                    onClick={() => openPaymentTerminal(order)}
                                                >
                                                    <i className="fas fa-cash-register me-2"></i> Complete Payment
                                                </button>
                                                <div className="d-flex gap-2">
                                                    <button className="btn btn-outline-warning d-flex align-items-center justify-content-center flex-grow-1 py-2 fw-bold" onClick={() => navigate(`/pos?addTo=${order._id}`)}>
                                                        <i className="fas fa-plus-circle me-2"></i> Add More Items
                                                    </button>
                                                    <button className="btn btn-outline-secondary px-3" onClick={() => printBill(order, 'payment', { discount: order.discount || 0, method: 'PAYMENT(LKR)' })} title="Print Bill">
                                                        <i className="fas fa-print"></i>
                                                    </button>
                                                    <button className="btn btn-outline-danger px-3 shadow-none border-0 bg-danger bg-opacity-10 rounded" onClick={() => handleCancelOrder(order._id)} title="Cancel Order">
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="table-responsive bg-white rounded-3 shadow-sm">
                                <table className="table dropdown-toggle-none table-hover border-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="px-4 py-3">Date & Time</th>
                                            <th>Order ID</th>
                                            <th>Type / Table</th>
                                            <th>Amount (LKR)</th>
                                            <th>Status</th>
                                            <th className="text-end px-4">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.map(order => (
                                            <React.Fragment key={order._id}>
                                                <tr className="align-middle" onClick={() => setExpandedOrderId(expandedOrderId === order._id ? null : order._id)} style={{cursor: 'pointer'}}>
                                                    <td className="px-4 py-3">
                                                        <i className={`fas fa-chevron-${expandedOrderId === order._id ? 'down' : 'right'} me-2 text-primary`} style={{width: '20px', transition: 'transform 0.2s'}}></i>
                                                        {new Date(order.createdAt).toLocaleString()}
                                                    </td>
                                                    <td><span className="text-primary fw-medium">{order.orderId}</span></td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border-0 px-3 py-2 rounded-pill shadow-xs">
                                                            {order.orderType} {order.tableNumber ? `- T${order.tableNumber}` : ''}
                                                        </span>
                                                    </td>
                                                    <td className="fw-bold">LKR {order.grandTotal.toFixed(2)}</td>
                                                    <td>
                                                        <span className={`badge ${order.status === 'Completed' ? 'bg-success' : order.status === 'Cancelled' ? 'bg-danger' : 'bg-warning'} px-3 py-2 rounded-pill`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                    <td className="text-end px-4">
                                                        <button className="btn btn-sm btn-icon btn-light" onClick={(e) => { e.stopPropagation(); printBill(order, 'payment', { discount: order.discount || 0, method: 'PAYMENT(LKR)' }); }}>
                                                            <i className="fas fa-print text-muted"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedOrderId === order._id && (
                                                    <tr>
                                                        <td colSpan="6" className="p-0 border-0">
                                                            <div className="p-4 border-top border-bottom" style={{backgroundColor: 'var(--orders-item-bg, #f8fafc)', margin: '0 1rem', borderRadius: '12px', marginBottom: '1rem', border: '1px solid var(--orders-border, #e2e8f0)'}}>
                                                                <div className="row">
                                                                    <div className="col-md-6 border-end-md">
                                                                        <h6 className="fw-bold text-uppercase mb-3" style={{fontSize: '0.75rem', letterSpacing: '1px', color: 'var(--orders-text-muted, #64748b)'}}>Order Items</h6>
                                                                        <div className="pe-md-4">
                                                                            {order.items.map((item, idx) => (
                                                                                <div className="d-flex justify-content-between mb-2 pb-2 border-bottom border-light align-items-center" key={idx}>
                                                                                    <div className="d-flex align-items-center">
                                                                                        <span className="badge text-primary border me-2" style={{fontSize: '0.75rem', backgroundColor: 'var(--orders-card, #ffffff)'}}>{item.quantity}</span>
                                                                                        <span className="fw-medium text-dark" style={{color: 'var(--orders-text, #212529) !important'}}>{item.productName}</span>
                                                                                    </div>
                                                                                    <span className="text-muted small">LKR {item.total.toFixed(2)}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6 mt-4 mt-md-0 ps-md-4">
                                                                        <h6 className="fw-bold text-uppercase mb-3" style={{fontSize: '0.75rem', letterSpacing: '1px', color: 'var(--orders-text-muted, #64748b)'}}>Order Summary</h6>
                                                                        <div className="p-3 rounded-3 border" style={{backgroundColor: 'var(--orders-card, #ffffff)'}}>
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="text-muted">Subtotal</span>
                                                                                <span className="text-dark" style={{color: 'var(--orders-text, #212529) !important'}}>LKR {(order.grandTotal + (order.discount || 0)).toFixed(2)}</span>
                                                                            </div>
                                                                            {order.discount > 0 && (
                                                                                <div className="d-flex justify-content-between mb-2 text-danger">
                                                                                    <span>Total Discount</span>
                                                                                    <span className="fw-bold">- LKR {order.discount.toFixed(2)}</span>
                                                                                </div>
                                                                            )}
                                                                            <div className="d-flex justify-content-between mt-3 pt-2 border-top">
                                                                                <span className="fw-bold text-dark" style={{color: 'var(--orders-text, #212529) !important'}}>Total Amount Paid</span>
                                                                                <span className="fw-bold text-primary h5 mb-0">LKR {order.grandTotal.toFixed(2)}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mt-3 p-3 border rounded-3 d-flex align-items-center gap-2" style={{backgroundColor: 'var(--orders-card, #ffffff)'}}>
                                                                            <i className="fas fa-info-circle text-info"></i>
                                                                            {order.status === 'Completed' ? (
                                                                                <span className="small text-muted">Order settled via <strong className="text-dark" style={{color: 'var(--orders-text, #212529) !important'}}>{order.paymentMethod || 'Cash'} Payment</strong> on {new Date(order.updatedAt || order.createdAt).toLocaleDateString()}</span>
                                                                            ) : (
                                                                                <span className="small text-muted">Order is currently <strong className="text-dark" style={{color: 'var(--orders-text, #212529) !important'}}>{order.status}</strong></span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Professional Payment Terminal Modal */}
            {showPaymentModal && paymentOrder && (
                <div className="modal fade show d-flex align-items-center justify-content-center" style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1050, backdropFilter: 'blur(4px)' }} tabIndex="-1">
                    <div className="modal-dialog modal-xl w-100" style={{ maxWidth: '900px' }}>
                        <div className="modal-content payment-terminal-modal border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            
                            {/* Header */}
                            <div className="modal-header d-flex justify-content-between align-items-center p-4" style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                <div>
                                    <h4 className="modal-title fw-bold text-white mb-1" style={{ fontSize: '1.25rem' }}>Payment Terminal</h4>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary px-2 py-1 rounded" style={{ fontSize: '0.75rem' }}>{paymentOrder.orderType}</span>
                                        <span className="text-light" style={{ fontSize: '0.85rem', opacity: 0.8 }}>KOT: <strong className="text-white">{paymentOrder.kotNumber}</strong></span>
                                        {paymentOrder.tableNumber && (
                                            <span className="text-light" style={{ fontSize: '0.85rem', opacity: 0.8 }}>â€¢ Table: <strong className="text-white">{paymentOrder.tableNumber}</strong></span>
                                        )}
                                    </div>
                                </div>
                                <button type="button" className="btn text-white rounded-circle d-flex align-items-center justify-content-center" onClick={() => setShowPaymentModal(false)} style={{ width: '36px', height: '36px', backgroundColor: 'rgba(255,255,255,0.1)', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="modal-body p-0">
                                <div className="row g-0">
                                    
                                    {/* Left Side: Order Summary */}
                                    <div className="col-md-5 d-flex flex-column payment-terminal-modal" style={{ borderRight: '1px solid var(--orders-border, #e2e8f0)', height: '100%' }}>
                                        <div className="p-4 flex-grow-1 payment-terminal-side">
                                            <h6 className="fw-bold text-uppercase mb-3" style={{ color: 'var(--orders-text-muted, #64748b)', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Order Details</h6>
                                            
                                            <div className="summary-list overflow-auto pe-2" style={{ maxHeight: '350px' }}>
                                                {paymentOrder.items.map((item, idx) => (
                                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom summary-list-item" key={idx}>
                                                        <div className="d-flex align-items-start gap-3">
                                                            <span className="fw-bold rounded text-dark d-flex align-items-center justify-content-center mt-1" style={{ width: '28px', height: '28px', fontSize: '0.8rem', backgroundColor: '#e2e8f0', border: '1px solid #cbd5e1' }}>{item.quantity}</span>
                                                            <div>
                                                                <div className="fw-bold text-dark" style={{ fontSize: '0.95rem', lineHeight: '1.2' }}>{item.productName}</div>
                                                                <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '2px' }}>LKR {(item.total / item.quantity).toFixed(2)} each</div>
                                                            </div>
                                                        </div>
                                                        <span className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>LKR {item.total.toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="p-4 payment-terminal-modal" style={{ borderTop: '1px dashed var(--orders-border, #cbd5e1)' }}>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted" style={{ fontSize: '0.95rem' }}>Subtotal</span>
                                                <span className="fw-bold text-dark text-end">LKR {paymentOrder.grandTotal.toFixed(2)}</span>
                                            </div>
                                            {effectiveDiscount > 0 && (
                                                <div className="d-flex justify-content-between mb-2 text-danger">
                                                    <span style={{ fontSize: '0.95rem' }}>Discount applied</span>
                                                    <span className="fw-bold text-end">- LKR {effectiveDiscount.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 pb-1" style={{ borderTop: '2px solid #f1f5f9' }}>
                                                <span className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Total to Pay</span>
                                                <span className="fw-bold text-primary" style={{ fontSize: '1.4rem' }}>LKR {totalToPay.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Side: Payment & Settlement */}
                                    <div className="col-md-7 payment-terminal-modal p-4 p-md-5 d-flex flex-column">
                                        
                                        {/* Coupon Section */}
                                        <div className="mb-4">
                                            <label className="fw-bold text-uppercase mb-2" style={{ color: 'var(--orders-text-muted, #475569)', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Apply Coupon</label>
                                            <div className="input-group">
                                                <span className="input-group-text payment-terminal-side border-end-0" style={{ border: '1px solid var(--orders-border, #cbd5e1)' }}><i className="fas fa-ticket-alt text-muted"></i></span>
                                                <input 
                                                    type="text" 
                                                    className="form-control border-start-0 py-2 pt-2 amount-input" 
                                                    placeholder="Enter promo code" 
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value)}
                                                    style={{ border: '1px solid var(--orders-border, #cbd5e1)', fontSize: '0.95rem', boxShadow: 'none' }}
                                                />
                                                <button className="btn btn-dark fw-bold px-4 pt-2" type="button" onClick={applyCoupon}>Apply</button>
                                            </div>
                                        </div>

                                        {/* Payment Method Section */}
                                        <div className="mb-4">
                                            <label className="fw-bold text-uppercase mb-2" style={{ color: 'var(--orders-text-muted, #475569)', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Payment Method</label>
                                            <select 
                                                className="form-select amount-input" 
                                                value={paymentMethod} 
                                                onChange={(e) => setPaymentMethod(e.target.value)} 
                                                style={{ border: '1px solid var(--orders-border, #cbd5e1)', fontSize: '1rem', padding: '10px 15px', borderRadius: '12px', boxShadow: 'none' }}
                                            >
                                                <option value="Cash">Cash (Manual Handling)</option>
                                                <option value="Card">Credit / Debit Card</option>
                                                <option value="Online">Online / Transfer</option>
                                            </select>
                                        </div>

                                        {paymentMethod === 'Cash' ? (
                                            <>
                                                {/* Cash Given Section */}
                                                <div className="mb-4">
                                                    <label className="fw-bold text-uppercase mb-2" style={{ color: 'var(--orders-text-muted, #475569)', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Amount Tendered</label>
                                                    
                                                    <div className="position-relative mb-3">
                                                        <span className="position-absolute top-50 translate-middle-y text-muted fw-bold ps-4" style={{ fontSize: '1.5rem', marginTop: '2px' }}>LKR</span>
                                                        <input 
                                                            type="number" 
                                                            min="0"
                                                            step="0.01"
                                                            className="form-control fw-bold amount-input" 
                                                            style={{ fontSize: '2rem', padding: '15px 15px 15px 75px', borderRadius: '12px', boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.02)' }}
                                                            placeholder="0.00" 
                                                            value={amountGiven || ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '') { setAmountGiven(0); return; }
                                                                const n = Number(v);
                                                                if (!Number.isFinite(n) || n < 0) return;
                                                                setAmountGiven(n);
                                                            }}
                                                            autoFocus
                                                        />
                                                    </div>

                                                    {/* Quick Cash Buttons */}
                                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                                        <button className="btn btn-outline-primary flex-grow-1 fw-bold py-2" style={{ borderRadius: '8px', border: '1px solid #cbd5e1', color: '#334155' }} onClick={() => setAmountGiven(totalToPay)}>Exact</button>
                                                        <button className="btn btn-outline-secondary flex-grow-1 fw-bold py-2" style={{ borderRadius: '8px', border: '1px solid #cbd5e1', color: '#334155' }} onClick={() => setAmountGiven(500)}>+ 500</button>
                                                        <button className="btn btn-outline-secondary flex-grow-1 fw-bold py-2" style={{ borderRadius: '8px', border: '1px solid #cbd5e1', color: '#334155' }} onClick={() => setAmountGiven(1000)}>+ 1,000</button>
                                                        <button className="btn btn-outline-secondary flex-grow-1 fw-bold py-2" style={{ borderRadius: '8px', border: '1px solid #cbd5e1', color: '#334155' }} onClick={() => setAmountGiven(5000)}>+ 5,000</button>
                                                    </div>
                                                </div>

                                                {/* Change Section */}
                                                <div className="mt-auto p-4 rounded-4 summary-list-item" style={{ backgroundColor: Number(calculateChange()) >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'var(--orders-item-bg, #f8fafc)', transition: 'all 0.3s' }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                                        <h6 className="text-uppercase fw-bold mb-0" style={{ color: Number(calculateChange()) >= 0 ? '#16a34a' : '#64748b', fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                                                            {Number(calculateChange()) < 0 ? 'Balance Due' : 'Change to Return'}
                                                        </h6>
                                                        <h2 className="mb-0 fw-bold" style={{ color: Number(calculateChange()) >= 0 ? '#16a34a' : '#ef4444', fontSize: '2.5rem' }}>
                                                            LKR {Number(calculateChange()) < 0 ? Math.abs(calculateChange()).toFixed(2) : calculateChange()}
                                                        </h2>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="mt-auto p-4 rounded-4 summary-list-item text-center mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px dashed rgba(59, 130, 246, 0.3)' }}>
                                                <div className="d-flex justify-content-center mb-3">
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm" style={{ width: '60px', height: '60px' }}>
                                                        <i className={`fas ${paymentMethod === 'Card' ? 'fa-credit-card' : 'fa-mobile-alt'} text-primary`} style={{ fontSize: '1.8rem' }}></i>
                                                    </div>
                                                </div>
                                                <h5 className="fw-bold text-dark mb-2">Complete payment externally</h5>
                                                <p className="text-muted small mb-0" style={{ lineHeight: '1.5' }}>Charge the customer <strong className="text-dark">LKR {totalToPay.toFixed(2)}</strong> on your terminal. Once completed, click &apos;Settle Order&apos; below.</p>
                                            </div>
                                        )}

                                        <div className="d-flex gap-3">
                                            <button className="btn btn-outline-dark fw-bold py-3 px-4 d-flex justify-content-center align-items-center gap-2 flex-grow-1" onClick={() => printBill(paymentOrder, 'payment', { discount: effectiveDiscount, method: `PAYMENT(${paymentMethod.toUpperCase()})` })} style={{ borderRadius: '10px' }}>
                                                <i className="fas fa-print"></i> Print Bill Layout
                                            </button>
                                            <button 
                                                className="btn btn-success fw-bold py-3 px-4 d-flex justify-content-center align-items-center gap-2 flex-grow-1" 
                                                style={{ borderRadius: '10px', fontSize: '1.1rem', boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.4)', transition: 'transform 0.1s, box-shadow 0.1s' }}
                                                onClick={() => settlePayment(paymentOrder._id)}
                                                disabled={paymentMethod === 'Cash' && (Number(calculateChange()) < 0 || (amountGiven === 0 && totalToPay > 0))}
                                                onMouseOver={(e) => {if(!e.currentTarget.disabled) e.currentTarget.style.transform = 'translateY(-2px)'}}
                                                onMouseOut={(e) => {if(!e.currentTarget.disabled) e.currentTarget.style.transform = 'translateY(0)'}}
                                            >
                                                <i className="fas fa-check-circle"></i> Settle Order
                                            </button>
                                        </div>
                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrdersManagement;
